import { useState } from 'react'
import { X, ClipboardList, Trash2, Send, MessageCircle } from 'lucide-react'
import { EmptyState } from './EmptyState'
import { waLink, COMPANY } from '../config'
import { submitInquiry } from '../data/api'

export function InquiryDrawer({ items, onClose, onRemove, onSubmit }) {
  const [form, setForm] = useState({ company: '', email: '', message: '' })
  const [submitted, setSubmitted] = useState(false)
  const [waUrl, setWaUrl] = useState('')
  const [delivered, setDelivered] = useState(false)
  const [sending, setSending] = useState(false)

  const buildMessage = () =>
    [
      `New quote request — ${COMPANY.legalName}`,
      '',
      `Company: ${form.company}`,
      `Email: ${form.email}`,
      `Message: ${form.message || '—'}`,
      '',
      `Items (${items.length}):`,
      items
        .map((item) => `• ${item.product.name} — ${item.qty} ${item.product.moqUnit || 'units'}`)
        .join('\n')
    ].join('\n')

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.company || !form.email || sending) return
    setSending(true)

    const url = waLink(buildMessage())
    setWaUrl(url)

    // Try backend delivery first — reaches admin's WhatsApp even if the
    // customer has no WhatsApp. Fall back to a wa.me deep link otherwise.
    const result = await submitInquiry({
      type: 'inquiry',
      company: form.company,
      contact: form.email,
      message: form.message,
      items: items.map((item) => ({
        name: item.product.name,
        qty: `${item.qty} ${item.product.moqUnit || 'units'}`
      }))
    })
    setDelivered(result.delivered)
    if (!result.delivered) window.open(url, '_blank')

    onSubmit?.({ ...form, items })
    setSending(false)
    setSubmitted(true)
  }

  if (submitted) {
    return (
      <>
        <div onClick={onClose} className="fixed inset-0 z-50 bg-brand-navy/40 backdrop-blur-sm" />
        <aside className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col items-center justify-center bg-white p-8 text-center shadow-drawer">
          <div className="rounded-full bg-green-50 p-4">
            <Send className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="mt-4 font-display text-2xl font-bold text-brand-ink">Quote request sent</h2>
          <p className="mt-2 text-sm text-brand-slate">
            {delivered
              ? 'Your request has been delivered to our team — we typically reply within one business day.'
              : 'Your request has been routed to our team on WhatsApp — we typically reply within one business day.'}
          </p>
          {waUrl && (
            <a
              href={waUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-white hover:brightness-95"
            >
              <MessageCircle className="h-4 w-4" />
              {delivered ? 'Chat with us on WhatsApp' : "WhatsApp didn't open? Tap here"}
            </a>
          )}
          <button onClick={onClose} className="mt-6 rounded-lg bg-brand-navy px-6 py-2.5 text-sm font-semibold text-white hover:bg-brand-ocean">
            Close
          </button>
        </aside>
      </>
    )
  }

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 z-50 bg-brand-navy/40 backdrop-blur-sm" />
      <aside className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col bg-white shadow-drawer">
        <div className="flex items-center justify-between border-b border-brand-slate/10 px-5 py-4">
          <h2 className="flex items-center gap-2 font-display text-xl font-bold text-brand-ink">
            <ClipboardList className="h-5 w-5 text-brand-gold" />
            Quote Request
          </h2>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-brand-shell" aria-label="Close inquiry">
            <X className="h-5 w-5 text-brand-slate" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {items.length === 0 ? (
            <EmptyState context="inquiry" actionLabel="Shop Wholesale" onAction={onClose} />
          ) : (
            <>
              <ul className="mb-6 space-y-4">
                {items.map((item) => (
                  <li key={item.product.id} className="flex items-start gap-3 rounded-xl bg-brand-shell p-3">
                    <img
                      src={item.product.image}
                      alt={item.product.name}
                      className="h-16 w-16 rounded-lg object-cover"
                    />
                    <div className="flex-1">
                      <h4 className="font-semibold text-brand-ink">{item.product.name}</h4>
                      <p className="text-xs text-brand-slate">
                        MOQ {item.product.moq} {item.product.moqUnit}
                      </p>
                    </div>
                    <button
                      onClick={() => onRemove(item.product.id)}
                      className="rounded-full p-2 text-brand-slate hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-brand-ink">Company name *</label>
                  <input
                    required
                    type="text"
                    value={form.company}
                    onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                    className="w-full rounded-lg border border-brand-slate/20 bg-white px-3 py-2 text-sm outline-none focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/20"
                    placeholder="e.g. Continental Foods Ltd"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-brand-ink">Email *</label>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    className="w-full rounded-lg border border-brand-slate/20 bg-white px-3 py-2 text-sm outline-none focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/20"
                    placeholder="procurement@company.com"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-brand-ink">Message</label>
                  <textarea
                    rows="4"
                    value={form.message}
                    onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                    className="w-full resize-none rounded-lg border border-brand-slate/20 bg-white px-3 py-2 text-sm outline-none focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/20"
                    placeholder="Volumes, destination port, delivery window, certifications..."
                  />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-gold py-3 text-sm font-bold text-brand-navy transition-colors hover:bg-brand-gold-light disabled:opacity-60"
                >
                  <Send className="h-4 w-4" />
                  {sending ? 'Sending…' : 'Request Quote'}
                </button>
                <p className="text-center text-xs text-brand-slate">
                  Sent instantly to our team — no WhatsApp account needed.
                </p>
              </form>
            </>
          )}
        </div>
      </aside>
    </>
  )
}
